import React, { useEffect, useState } from 'react'
import PageHeadSection from '../admin/components/PageHeadSection'
import ItemsApp from './ItemsApp'
import SectionBoxContainer from '../../components/SectionBoxContainer'
import { Box } from '@mui/system'
import { Button, Checkbox, FormControlLabel, Step, StepContent, StepLabel, Stepper, TextField, Typography } from '@mui/material'
import { addNewWarehouse, updateAllWarehouses, updateWarehousesPagination, useReturnAllWarehouses } from '../warehouses/warehousesSlice'
import WarehousesApp, { IWarehouseModel } from '../warehouses/WarehousesApp'
import CardItem from '../../components/DataDisplayer/CardItem'
import { useDispatch } from 'react-redux'
import { ISearchParam } from '../BaseApp'
import { handleRequestError } from '../../services/api'
import { IDataDisplayerItemField } from '../../components/DataDisplayer/DataDisplayer'
import SectionsApp, { ISectionModel } from '../sections/SectionsApp'
import DynamicForm, { IDynamicForm, IRequiredField } from '../../components/Form/DynamicForm'
import { addNewForm, createOrUpdateElementData } from '../../components/Form/formsSlice'
import GenerateForm from '../../components/Form/GenerateForm'
import { IAxiosDefaultDataObject } from '../../config'
import AlertElement from '../../components/AlertElement'
import ItemAdditionalData from './ItemAdditionalData'
import ItemAdditionalDataForm from './ItemAdditionalDataForm'

interface IProps {
    hidePageHeadBox?: boolean
}

interface IStep {
    label: string,
    content: (index: number) => JSX.Element,
}

export default function CreateItemPage(props: IProps) {

    const dispatch = useDispatch()

    const { hidePageHeadBox } = props

    const itemsApp = new ItemsApp()

    const form = itemsApp.formConfig.form
    const currentForm = DynamicForm.useReturnSingleForm(form.name)

    const uniqueIdForm: IDynamicForm = {
        name: "items_unique_id_form",
        elements: [
            {
                id: itemsApp.elemPrefix + "unique_id",
                xs: 12,
                type: "TextField",
                label: "Custom Unique ID",
                fullWidth: true,
                enableQrCodeScanner: true,
                validateInput: (text: string) => itemsApp.validateUniqueId(text),
                placeholder: "01D23DAE-0384-43DB-A"
            },
        ]
    }

    const elementsData = DynamicForm.useReturnAllFormsData()
    const singleElemDataValue = (name: string) => DynamicForm.returnSingleFormElementData(itemsApp.elemPrefix + name, elementsData).value


    /**
     * Create form elem data
     */
    const createFormElemData = () => {
        form.elements.map((elem) => dispatch(createOrUpdateElementData({
            name: elem.id,
            value: ""
        })))

        uniqueIdForm.elements.map((elem) => dispatch(createOrUpdateElementData({
            name: elem.id,
            value: ""
        })))
    }

    interface IWarehouseLocation {
        title: string,
        id: number,
    }

    const [activeStep, setActiveStep] = useState<number>(0)
    const [loading, setLoading] = useState<boolean>(true)

    const [warehouseId, setWarehouseId] = useState<number>(0)
    const [sectionId, setSectionId] = useState<number>(0)
    const [quantity, setQuantity] = useState<number | null>(1)
    const [isContainer, setIsContainer] = useState<boolean>(false)

    const [warehouses, setWarehouses] = useState<IWarehouseModel[]>([])
    const [warehouseLocations, setWarehouseLocations] = useState<IWarehouseLocation[]>([])
    const [activeWarehouseLocation, setActiveWarehouseLocation] = useState<number>(0)

    const warehousesApp = new WarehousesApp()

    const sectionsApp = new SectionsApp()
    const [sections, setSections] = useState<ISectionModel[]>([])

    const [categoryTitle, setCategoryTitle] = useState<string>("")

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }
    
    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1)
    }

    /**
     * Load warehouses
     */
    const loadWarehouses = () => {
        setLoading(true)
        warehousesApp.listOfInstances(1, "", true, false, [])
            .then(response => {

                const { data } = response
                setWarehouses(data)
                setLoading(false)

            })
            .catch(error => {
                handleRequestError(error, dispatch)
                setLoading(false)
            })
    }

    /**
     * Load sections
     */
    const loadSections = () => {
        setLoading(true)

        if (warehouseId){
            sectionsApp.listOfInstances(1, "", true, false, [{ name: "warehouse_id", value: warehouseId }])
                .then(response => {
                    setSections(response.data)
                    setLoading(false)
                })
                .catch(error => {
                    handleRequestError(error, dispatch)
                    setLoading(false)
                })
        }

    }

    /**
     * Render loading elements
     */
    const renderLoadingCards = () => {
        return(
            <>
                <CardItem id={0} fields={[]} rawItem={{}} thumbnail="" loading />
                <CardItem id={0} fields={[]} rawItem={{}} thumbnail="" loading />
                <CardItem id={0} fields={[]} rawItem={{}} thumbnail="" loading />
                <CardItem id={0} fields={[]} rawItem={{}} thumbnail="" loading />
                <CardItem id={0} fields={[]} rawItem={{}} thumbnail="" loading />
                <CardItem id={0} fields={[]} rawItem={{}} thumbnail="" loading />
            </>
        )
    }

    /**
     * Handle submit
     */
    const handleSubmit = () => {

        let data: IAxiosDefaultDataObject = {
            title: singleElemDataValue("title"),
            warehouse_id: warehouseId,
            section_id: sectionId,
            category_id: singleElemDataValue("category_id"),
            brand_id: singleElemDataValue("brand_id"),
            description: singleElemDataValue("description"),
            serial_number: singleElemDataValue("serial_number"),
            unique_id: singleElemDataValue("unique_id"),
            thumbnail: singleElemDataValue("thumbnail"),
            quantity: quantity,
            is_container: isContainer,
            weight: singleElemDataValue("weight"),
            item_model: singleElemDataValue("item_model"),
            watts: singleElemDataValue("watts"),
            images: singleElemDataValue("images"),
        }

        let requiredFields: Array<IRequiredField> = itemsApp.formConfig.requiredFields

        DynamicForm.resetRequiredFieldsHelperText(requiredFields, elementsData, dispatch)
        const verifyForm = DynamicForm.verifyForm(elementsData, requiredFields)
        
        data = itemsApp.updateItemSubmitData(data, singleElemDataValue, categoryTitle)

        if (verifyForm.isValid){
            DynamicForm.updateFormLoadingStatus(form.name, true, dispatch)

            itemsApp.createNewInstance(data)
                .then(response => {

                    const { data } = response
                    const message = data.message ? data.message : "Success!"

                    DynamicForm.updateFormLoadingStatus(form.name, false, dispatch)
                    DynamicForm.createOrUpdateFormMessage(form.name, { shown: true, type: "success", message: message }, dispatch)

                    window.location.replace("/panel/items")

                })
                .catch(error => {

                    console.log(error)
                    const message = error.response.data.message ? error.response.data.message : "Fatal Error"

                    DynamicForm.updateFormLoadingStatus(form.name, false, dispatch)
                    DynamicForm.createOrUpdateFormMessage(form.name, { shown: true, type: "error", message: message }, dispatch)

                })
        }else{
            DynamicForm.updateRequiredFieldsHelperText(verifyForm.fields, elementsData, dispatch)
        }

    }

    /**
     * Render stepper buttons
     */
    const renderStepperButtons = (index: number, disabledNext: boolean = false) => {

        let disableBack: boolean = index === 0

        return(
            <div className="create-new-item-step-next-buttons-container">
                <Box sx={{ mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => index === steps.length - 1 ? handleSubmit() : handleNext()}
                        sx={{ mt: 1, mr: 1 }}
                        disabled={disabledNext}
                    >
                        {index === steps.length - 1 ? 'Finish' : 'Continue'}
                    </Button>
                    <Button
                        disabled={disableBack}
                        onClick={handleBack}
                        sx={{ mt: 1, mr: 1 }}
                    >
                        Back
                    </Button>
                </Box>
            </div>
        )
    }

    /**
     * Render select type
     */
    const renderSelectType = (index: number) => {

        return(
            <div>
                <FormControlLabel control={<Checkbox checked={isContainer} onChange={(e) => setIsContainer(e.target.checked)} inputProps={{ 'aria-label': 'controlled' }} />} label="Is it container?" />
                {/* <AlertElement type="warning" message="" /> */}
                {renderStepperButtons(index, false)}
            </div>
        )
    }

    /**
     * Render warehouses
     */
    const renderWarehouses = () => {            
        return warehouses.map((warehouse, index) => {

            let fields: IDataDisplayerItemField[] = [
                {
                    id: "title",
                    type: "title",
                    label: "Title",
                    value: warehouse.title
                },
                {
                    id: "title",
                    type: "text",
                    label: "Location",
                    value: warehouse.location_title,
                    showLabel: true
                }
            ]

            if (warehouse.location_id !== activeWarehouseLocation){
                return <></>
            }

            return <CardItem id={warehouse.id} rawItem={warehouse} thumbnail={warehouse.thumbnail} fields={fields} key={index} onClick={(data) => {
                setWarehouseId(data.id)
                handleNext()
            }} />
        })
    }

    /**
     * Render sections
     */
    const renderSections = () => {
        
        return sections.map((section, index) => {

            let fields: IDataDisplayerItemField[] = [
                {
                    id: "title",
                    type: "title",
                    label: "Title",
                    value: section.title
                },
                {
                    id: "title",
                    type: "text",
                    label: "Warehouse",
                    value: section.warehouse_title,
                    showLabel: true
                }
            ]

            return <CardItem id={section.id} rawItem={section} thumbnail={section.thumbnail} fields={fields} key={index} onClick={(data) => {
                setSectionId(data.id)
                handleNext()
            }} />
        })
    }

    /**
     * Warehouse step
     */
    const warehouseStep = (index: number) => {

        return(
            <div>

                <div className="warehouse-location-options-container">
                    <div className="content-of-warehouse-location-options-container">
                        {warehouseLocations.map((loc, index) => {
                            let spanClassName: string = "war-loc-option-box"

                            if (loc.id == activeWarehouseLocation){
                                spanClassName += " active"
                            }

                            return <span className={spanClassName} onClick={() => setActiveWarehouseLocation(loc.id)}>{loc.title}</span>
                        })}
                    </div>
                </div>

                <div className="page-container-items">
                    <div className="content-of-page-container-items">
                        {loading ? renderLoadingCards() : renderWarehouses()}
                    </div>
                </div>

                {renderStepperButtons(index, warehouseId ? false : true)}
            </div>
        )
    }

    /**
     * Section step
     */
    const sectionStep = (index: number) => {
        return(
            <div>

                <div className="page-container-items">
                    <div className="content-of-page-container-items">
                        {loading ? renderLoadingCards() : renderSections()}
                    </div>
                </div>

                {renderStepperButtons(index, sectionId ? false : true)}
            </div>
        )
    }

    /**
     * Details step
     */
    const detailsStep = (index: number) => {
        return(
            <div>
                <GenerateForm name={form.name} />

                <ItemAdditionalDataForm item={null} handleUpdateSubmitCategoryTitle={setCategoryTitle} />
               
                <div style={{ paddingTop: 10 }}>
                    <GenerateForm name={uniqueIdForm.name} />
                </div>

                {renderStepperButtons(index, false)}
            </div>
        )
    }

    /**
     * Unqiue Id step
     */
    const unqiueIdStep = (index: number) => {
        return(
            <div>
                <GenerateForm name={uniqueIdForm.name} />
                {renderStepperButtons(index, false)}
            </div>
        )
    }

    /**
     * Quantity 
     */
    const quantityStep = (index: number) => {
        return(
            <div>
                <TextField disabled={isContainer} type="number" variant="outlined" fullWidth label="Quantity" value={quantity} onChange={(e) => {
                    const value = e.target.value
                    setQuantity(value === "" ? null : Number(value))
                }} />
                {isContainer && <div style={{ marginTop: 10 }}><AlertElement type='warning' message="Quantity must be 1 if the item is a container." /></div>}
                {renderStepperButtons(index, quantity ? quantity > 0 ? false : true : true)}
            </div>
        )
    }

    /**
     * Steps
     */
    let steps: IStep[] = [
        {
            label: "Select Warehouse",
            content: warehouseStep
        },
        {
            label: "Select Section",
            content: sectionStep
        },
        {
            label: "Item Type",
            content: renderSelectType
        },
        {
            label: "Quantity",
            content: quantityStep
        },
        // {
        //     label: "Custom Unique ID",
        //     content: unqiueIdStep
        // },
        {
            label: "Details",
            content: detailsStep
        },
    ]

    /**
     * Load sections
     */
    useEffect(() => {
        loadSections()
    }, [warehouseId])

    /**
     * Load
     */
    useEffect(() => {
        loadWarehouses()
    }, [])

    useEffect(() => {

        let warehouseLocationsList: IWarehouseLocation[] = []

        warehouses.map((warehouse) => {
            console.log(warehouse.title, warehouse.location_id)
            if (!warehouseLocationsList.some(loc => loc.id === warehouse.location_id)) {
                warehouseLocationsList.push({
                    title: warehouse.location_title,
                    id: warehouse.location_id
                })
            }
        })

        setWarehouseLocations(warehouseLocationsList)

        if (warehouseLocationsList.length > 0){
            setActiveWarehouseLocation(warehouseLocationsList[0].id)
        }

    }, [warehouses])

    /**
     * Create Form
     */
    useEffect(() => {

        dispatch(addNewForm({
            name: form.name,
            elements: form.elements
        }))

        dispatch(addNewForm({
            name: uniqueIdForm.name,
            elements: uniqueIdForm.elements
        }))

        createFormElemData()

    }, [])

    return(
        <div>

            {!hidePageHeadBox && <PageHeadSection customPageType="new" pageName="items" />}

            <SectionBoxContainer
                showTitle={itemsApp.formConfig.sectionBox.showTitle}
                title={itemsApp.formConfig.sectionBox.title}
                description={itemsApp.formConfig.sectionBox.description}
            >
                <Stepper activeStep={activeStep} orientation="vertical">
                    {steps.map((step, index) => {
                        return (
                            <Step key={step.label}>
                                <StepLabel
                                    optional={
                                        index === steps.length - 1 ? (
                                        <Typography variant="caption">Last step</Typography>
                                        ) : null
                                    }
                                >
                                    {step.label}
                                </StepLabel>

                                <StepContent>

                                    <div className="create-new-item-step-content-container">
                                        <div className="content-of-create-new-item-step-content-container">
                                            {step.content(index)}
                                        </div>
                                    </div>
                                    
                                </StepContent>
                            </Step>
                        )
                    })}
                </Stepper>
            </SectionBoxContainer>

        </div>
    )
}
